import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PageHeader } from '../design';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { 
  DollarSign, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Edit, 
  RotateCcw,
  Filter,
  Search,
  X,
  Download,
  TrendingUp,
  TrendingDown,
  Clock,
  Building2,
  Users,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Check,
  History,
  Bell,
  Settings,
  Info,
  Zap,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

/**
 * Enhanced Budgets Page - Governance Panel
 * 
 * Features:
 * - Platform-wide budget overview with trends
 * - Real tenant names and organization context
 * - Severity-based color coding
 * - Spending breakdown by service
 * - Bulk actions for alerts
 * - Export to CSV
 * - Pagination
 * - Audit trail
 * - Auto-refresh
 */
export default function Budgets() {
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('30d');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Selection for bulk actions
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  
  // Dialog states
  const [editDialog, setEditDialog] = useState({ open: false, tenant: null });
  const [resetDialog, setResetDialog] = useState({ open: false, tenant: null });
  const [settingsDialog, setSettingsDialog] = useState(false);
  
  // Form states
  const [budgetForm, setBudgetForm] = useState({
    monthly_total: 500,
    llm: 200,
    twilio: 200,
    elevenlabs: 100,
    auto_pause_enabled: true,
    auto_pause_threshold: 1.0,
    notification_threshold: 0.8
  });
  
  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const response = await governanceAPI.getEnhancedBudgetDashboard(
        timeRange,
        severityFilter,
        currentPage,
        pageSize
      );
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[Budgets] Failed to fetch:', err);
      setError('Failed to load budget data. Please try again.');
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [timeRange, severityFilter, currentPage, pageSize]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, isAuthenticated, fetchData]);

  // Filter tenant budgets by search
  const filteredTenantBudgets = useMemo(() => {
    if (!dashboardData?.tenant_budgets) return [];
    
    return dashboardData.tenant_budgets.filter(tb => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!tb.tenant_name?.toLowerCase().includes(query) && 
            !tb.tenant_id?.toLowerCase().includes(query) &&
            !tb.organization_name?.toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [dashboardData?.tenant_budgets, searchQuery]);

  // Filter alerts by status
  const filteredAlerts = useMemo(() => {
    if (!dashboardData?.alerts) return [];
    
    return dashboardData.alerts.filter(alert => {
      if (statusFilter !== 'all' && alert.status !== statusFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!alert.tenant_name?.toLowerCase().includes(query) && 
            !alert.tenant_id?.toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [dashboardData?.alerts, statusFilter, searchQuery]);

  const acknowledge = async (alertId) => {
    try {
      await governanceAPI.acknowledgeBudgetAlert(alertId);
      toast.success('Alert marked as reviewed');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const bulkAcknowledge = async () => {
    if (selectedAlerts.length === 0) {
      toast.error('No alerts selected');
      return;
    }
    
    try {
      await governanceAPI.bulkAcknowledgeAlerts(selectedAlerts);
      toast.success(`${selectedAlerts.length} alerts marked as reviewed`);
      setSelectedAlerts([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to bulk acknowledge alerts');
    }
  };

  const openEditDialog = async (tenantBudget) => {
    setBudgetForm({
      monthly_total: tenantBudget.monthly_limit || 500,
      llm: tenantBudget.services?.llm?.limit || 200,
      twilio: tenantBudget.services?.twilio?.limit || 200,
      elevenlabs: tenantBudget.services?.elevenlabs?.limit || 100,
      auto_pause_enabled: true,
      auto_pause_threshold: 1.0,
      notification_threshold: 0.8
    });
    setEditDialog({ open: true, tenant: tenantBudget });
  };

  const handleSaveBudget = async () => {
    try {
      await governanceAPI.updateTenantBudget(editDialog.tenant.tenant_id, budgetForm);
      toast.success(`Budget updated for ${editDialog.tenant.tenant_name}`);
      setEditDialog({ open: false, tenant: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update budget');
    }
  };

  const handleResetBudget = async () => {
    try {
      await governanceAPI.resetTenantBudget(resetDialog.tenant.tenant_id);
      toast.success(`Budget cycle reset for ${resetDialog.tenant.tenant_name}`);
      setResetDialog({ open: false, tenant: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset budget');
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!dashboardData?.tenant_budgets) return;
    
    const headers = ['Tenant Name', 'Organization', 'Monthly Limit', 'Current Usage', 'Remaining', 'Usage %', 'Severity', 'Daily Burn Rate', 'Projected Spend'];
    const rows = dashboardData.tenant_budgets.map(tb => [
      tb.tenant_name,
      tb.organization_name,
      tb.monthly_limit,
      tb.current_usage,
      tb.remaining,
      tb.usage_percent,
      tb.severity,
      tb.daily_burn_rate,
      tb.projected_spend
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budget-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Budget report exported');
  };

  // Get severity color classes
  const getSeverityColor = (severity, forProgress = false) => {
    switch (severity) {
      case 'critical':
        return forProgress ? 'bg-red-500' : 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20 border-red-200 dark:border-red-500/50';
      case 'high':
        return forProgress ? 'bg-orange-500' : 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-500/20 border-orange-200 dark:border-orange-500/50';
      case 'warning':
        return forProgress ? 'bg-amber-500' : 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20 border-amber-200 dark:border-amber-500/50';
      default:
        return forProgress ? 'bg-green-500' : 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20 border-green-200 dark:border-green-500/50';
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      warning: 'bg-amber-500 text-white',
      healthy: 'bg-green-500 text-white'
    };
    const labels = {
      critical: 'Critical',
      high: 'High Risk',
      warning: 'Warning',
      healthy: 'Healthy'
    };
    return <Badge className={colors[severity] || colors.healthy}>{labels[severity] || 'Healthy'}</Badge>;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6" data-testid="budgets-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6" data-testid="budgets-error">
        <Alert className="border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-950/20">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertTitle className="text-red-700 dark:text-red-300">Error Loading Data</AlertTitle>
          <AlertDescription className="text-red-600 dark:text-red-400">
            {error}
          </AlertDescription>
        </Alert>
        <Button onClick={() => { setLoading(true); fetchData(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};
  const severitySummary = dashboardData?.severity_summary || {};
  const serviceBreakdown = dashboardData?.service_breakdown || {};
  const spendingTrend = dashboardData?.spending_trend || [];
  const auditTrail = dashboardData?.audit_trail || [];
  const pagination = dashboardData?.pagination || {};

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="budgets">
        {/* Header */}
        <PageHeader
          title="Budget Management"
          subtitle={`${overview.period?.label || 'Current Month'} • Last updated: ${lastRefresh.toLocaleTimeString()}`}
          actions={
            <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={exportToCSV} className="border-slate-200 dark:border-slate-700">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download budget report as CSV</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={autoRefresh ? 'border-green-500 text-green-600' : 'border-slate-200 dark:border-slate-700'}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                  {autoRefresh ? 'Auto' : 'Manual'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{autoRefresh ? 'Auto-refresh every 30s' : 'Click to enable auto-refresh'}</TooltipContent>
            </Tooltip>
            <Button variant="outline" size="icon" onClick={() => { setLoading(true); fetchData(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          }
        />

        {/* Budget Overview Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Budget</p>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${overview.total_budget?.toLocaleString() || '0'}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Monthly allocation
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Spent</p>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${overview.total_spent?.toLocaleString() || '0'}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {overview.month_over_month_change > 0 ? (
                      <TrendingUp className="h-3 w-3 text-red-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-green-500" />
                    )}
                    <span className={`text-xs ${overview.month_over_month_change > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {Math.abs(overview.month_over_month_change || 0).toFixed(1)}% vs last month
                    </span>
                  </div>
                </div>
                <div className="h-12 w-12 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Remaining</p>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${overview.total_remaining?.toLocaleString() || '0'}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {overview.days_remaining_in_month || 0} days left
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Burn Rate</p>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    ${overview.daily_burn_rate?.toFixed(2) || '0'}/day
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Projected: ${overview.projected_month_end?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Severity Summary & Spending Trend */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Severity Summary */}
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Alerts by Severity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{severitySummary.critical || 0}</div>
                  <p className="text-xs text-red-500 dark:text-red-400">Critical (≥100%)</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{severitySummary.high || 0}</div>
                  <p className="text-xs text-orange-500 dark:text-orange-400">High (90-99%)</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{severitySummary.warning || 0}</div>
                  <p className="text-xs text-amber-500 dark:text-amber-400">Warning (80-89%)</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{severitySummary.healthy || 0}</div>
                  <p className="text-xs text-green-500 dark:text-green-400">Healthy (&lt;80%)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spending Trend Chart */}
          <Card className="lg:col-span-2 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Spending Trend
                </CardTitle>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-24 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">7 days</SelectItem>
                    <SelectItem value="14d">14 days</SelectItem>
                    <SelectItem value="30d">30 days</SelectItem>
                    <SelectItem value="90d">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-32 flex items-end gap-1">
                {spendingTrend.map((day, idx) => {
                  const maxAmount = Math.max(...spendingTrend.map(d => d.amount), 1);
                  const heightPercent = (day.amount / maxAmount) * 100;
                  return (
                    <Tooltip key={idx}>
                      <TooltipTrigger asChild>
                        <div className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-blue-500 dark:bg-blue-400 rounded-t transition-all hover:bg-blue-600"
                            style={{ height: `${Math.max(heightPercent, 2)}%` }}
                          />
                          <span className="text-[10px] text-slate-400 truncate w-full text-center">
                            {day.label?.split(' ')[1] || ''}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-medium">{day.label}</p>
                        <p className="text-sm">${day.amount.toFixed(2)}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Service Breakdown */}
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Spending by Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">LLM</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Language Models</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">OpenAI, Claude, etc.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 dark:text-white">${serviceBreakdown.llm?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-sm">📞</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Twilio</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">SMS & Voice</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 dark:text-white">${serviceBreakdown.twilio?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-600 dark:text-green-400 font-bold text-sm">🎙️</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">ElevenLabs</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Text-to-Speech</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 dark:text-white">${serviceBreakdown.elevenlabs?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Budgets / Alerts / Audit */}
        <Tabs defaultValue="budgets" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="budgets" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Tenant Budgets
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alerts
              {filteredAlerts.filter(a => a.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1">
                  {filteredAlerts.filter(a => a.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Tenant Budgets Tab */}
          <TabsContent value="budgets" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-base text-slate-900 dark:text-white">
                    Tenant Budget Status ({pagination.total_count || 0})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search tenants..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 w-64"
                      />
                    </div>
                    <Select value={severityFilter} onValueChange={setSeverityFilter}>
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue placeholder="Severity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Severity</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="healthy">Healthy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredTenantBudgets.length === 0 ? (
                  <div className="py-12 text-center">
                    <DollarSign className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">No Tenants Found</p>
                    <p className="text-slate-500 dark:text-slate-400">
                      {searchQuery ? 'No tenants match your search' : 'No tenant budgets configured'}
                    </p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tenant</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Spent / Limit</TableHead>
                          <TableHead className="text-right">Burn Rate</TableHead>
                          <TableHead className="text-right">Projected</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTenantBudgets.map((tb) => (
                          <TableRow key={tb.tenant_id} className={`${getSeverityColor(tb.severity).includes('red') ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{tb.tenant_name}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{tb.organization_name}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getSeverityBadge(tb.severity)}
                                <span className="text-sm font-medium">{tb.usage_percent}%</span>
                              </div>
                              <div className="mt-1 w-32">
                                <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                  <div 
                                    className={`h-full transition-all ${getSeverityColor(tb.severity, true)}`}
                                    style={{ width: `${Math.min(tb.usage_percent, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <p className="font-medium text-slate-900 dark:text-white">
                                ${tb.current_usage?.toFixed(2)} / ${tb.monthly_limit}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                ${tb.remaining?.toFixed(2)} remaining
                              </p>
                            </TableCell>
                            <TableCell className="text-right">
                              <p className="font-medium text-slate-900 dark:text-white">
                                ${tb.daily_burn_rate}/day
                              </p>
                            </TableCell>
                            <TableCell className="text-right">
                              <p className={`font-medium ${tb.projected_spend > tb.monthly_limit ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
                                ${tb.projected_spend}
                              </p>
                              {tb.projected_spend > tb.monthly_limit && (
                                <p className="text-xs text-red-500">Over budget!</p>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(tb)}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit budget limits</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setResetDialog({ open: true, tenant: tb })}>
                                      <RotateCcw className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Reset budget cycle</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Page {pagination.page} of {pagination.total_pages}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))}
                            disabled={currentPage === pagination.total_pages}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-base text-slate-900 dark:text-white">
                    Budget Alerts ({filteredAlerts.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40 h-9">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="acknowledged">Reviewed</SelectItem>
                      </SelectContent>
                    </Select>
                    {selectedAlerts.length > 0 && (
                      <Button size="sm" onClick={bulkAcknowledge}>
                        <Check className="h-4 w-4 mr-2" />
                        Mark {selectedAlerts.length} as Reviewed
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredAlerts.length === 0 ? (
                  <div className="py-12 text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">No Alerts</p>
                    <p className="text-slate-500 dark:text-slate-400">All budget alerts have been reviewed</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredAlerts.map((alert) => (
                      <div 
                        key={alert._id}
                        className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}
                      >
                        <div className="flex items-center gap-4">
                          <Checkbox
                            checked={selectedAlerts.includes(alert._id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAlerts([...selectedAlerts, alert._id]);
                              } else {
                                setSelectedAlerts(selectedAlerts.filter(id => id !== alert._id));
                              }
                            }}
                            disabled={alert.status === 'acknowledged'}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-slate-900 dark:text-white">{alert.tenant_name}</p>
                              <Badge variant="outline">{alert.service}</Badge>
                              {getSeverityBadge(alert.severity)}
                              {alert.status === 'acknowledged' && (
                                <Badge variant="secondary">Reviewed</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300">
                              ${alert.usage?.toFixed(2)} / ${alert.limit?.toFixed(2)} ({alert.percentage}%)
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                              {alert.acknowledged_by && ` • Reviewed by ${alert.acknowledged_by}`}
                            </p>
                          </div>
                          {alert.status !== 'acknowledged' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => acknowledge(alert._id)}>
                                  <Check className="h-4 w-4 mr-1" />
                                  Mark as Reviewed
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Acknowledge this alert</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-900 dark:text-white">
                  Budget Action History
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  Recent budget updates, resets, and acknowledgements
                </CardDescription>
              </CardHeader>
              <CardContent>
                {auditTrail.length === 0 ? (
                  <div className="py-12 text-center">
                    <History className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">No Activity</p>
                    <p className="text-slate-500 dark:text-slate-400">No budget actions recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditTrail.map((log) => (
                      <div key={log.id} className="flex items-start gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                          {log.action === 'budget_update' && <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                          {log.action === 'budget_reset' && <RotateCcw className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                          {log.action === 'budget_alert_acknowledge' && <Check className="h-4 w-4 text-green-600 dark:text-green-400" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 dark:text-white">
                              {log.action === 'budget_update' && 'Budget Updated'}
                              {log.action === 'budget_reset' && 'Budget Reset'}
                              {log.action === 'budget_alert_acknowledge' && 'Alert Acknowledged'}
                            </p>
                            {log.tenant_name && (
                              <Badge variant="outline">{log.tenant_name}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            By {log.performed_by} • {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Budget Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit Budget Limits
              </DialogTitle>
              <DialogDescription>
                Adjust budget limits for <strong>{editDialog.tenant?.tenant_name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="monthly_total">Monthly Total Limit (USD)</Label>
                <Input
                  id="monthly_total"
                  type="number"
                  value={budgetForm.monthly_total}
                  onChange={(e) => setBudgetForm({...budgetForm, monthly_total: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="llm">LLM</Label>
                  <Input
                    id="llm"
                    type="number"
                    value={budgetForm.llm}
                    onChange={(e) => setBudgetForm({...budgetForm, llm: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio">Twilio</Label>
                  <Input
                    id="twilio"
                    type="number"
                    value={budgetForm.twilio}
                    onChange={(e) => setBudgetForm({...budgetForm, twilio: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="elevenlabs">ElevenLabs</Label>
                  <Input
                    id="elevenlabs"
                    type="number"
                    value={budgetForm.elevenlabs}
                    onChange={(e) => setBudgetForm({...budgetForm, elevenlabs: parseFloat(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto_pause">Auto-Pause on Breach</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Automatically pause AI when budget exceeded</p>
                  </div>
                  <Switch
                    id="auto_pause"
                    checked={budgetForm.auto_pause_enabled}
                    onCheckedChange={(checked) => setBudgetForm({...budgetForm, auto_pause_enabled: checked})}
                  />
                </div>
              </div>
              
              {budgetForm.auto_pause_enabled && (
                <div>
                  <Label htmlFor="threshold">Auto-Pause Threshold</Label>
                  <Select 
                    value={budgetForm.auto_pause_threshold.toString()}
                    onValueChange={(v) => setBudgetForm({...budgetForm, auto_pause_threshold: parseFloat(v)})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.8">80% of limit</SelectItem>
                      <SelectItem value="0.9">90% of limit</SelectItem>
                      <SelectItem value="1.0">100% of limit</SelectItem>
                      <SelectItem value="1.1">110% of limit (grace)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="notification_threshold">Notification Threshold</Label>
                <Select 
                  value={budgetForm.notification_threshold.toString()}
                  onValueChange={(v) => setBudgetForm({...budgetForm, notification_threshold: parseFloat(v)})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">50% - Early warning</SelectItem>
                    <SelectItem value="0.7">70% - Moderate</SelectItem>
                    <SelectItem value="0.8">80% - Standard</SelectItem>
                    <SelectItem value="0.9">90% - Late warning</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Send notification when budget reaches this threshold
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, tenant: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveBudget}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Budget Dialog */}
        <Dialog open={resetDialog.open} onOpenChange={(open) => setResetDialog({ ...resetDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Reset Budget Cycle
              </DialogTitle>
              <DialogDescription>
                This will reset all usage counters for <strong>{resetDialog.tenant?.tenant_name}</strong> to zero. 
                Budget limits will remain unchanged. This action is typically used for billing cycle corrections.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Alert className="border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-700 dark:text-amber-300">
                  <strong>Warning:</strong> This action cannot be undone. All current month usage data will be reset.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setResetDialog({ open: false, tenant: null })}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleResetBudget}>
                Reset Budget Cycle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
